import { TaskUrgency } from '@mimir/common/async/taskPriority';
import { makePriority, PrioritySemaphore } from '@mimir/common/async/prioritySemaphore';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import type { DynamoItemType} from '@mimir/common/dto/itemTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { FileSetProgress, FileSetListener } from '@mimir/common/upload/uploadProgression';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { ItemRestoring, RestoreMediaModel} from '@mimir/common/dto/restoreMediaDto';
import { RestoreTier } from '@mimir/common/dto/restoreMediaRequestDto';
import type { ItemArtefact} from '@mimir/common/dto/transferRequestDtos';
import type { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import type { FileApi, FilePath } from '@mimir/common/upload/fileApi';
import { UploadState } from '@mimir/common/upload/upload';
import {
  itemTypeFromFilename,
  itemTypeFromMediaType,
  mediaTypeFromFilename,
} from '@mimir/common/util/mediaContentType';
import { invariant } from '@mimir/common/util/invariant';
import { exceptionEssence, makeError } from '@mimir/common/util/exception';
import {
  copyFile,
  deleteFile,
  getLocalFileSize,
  hardLinkOrCopy,
  isDirAvailableLocally,
  isFileAvailableLocally,
  recursiveMkdirSync
} from '@mimir/common/util/fs';
import { wasHighresDeleted } from '@mimir/common/util/item';
import { isMediaOnline } from '@mimir/common/item/media';
import type { CancelToken } from 'axios';
import Axios from 'axios';
import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import type { ArtifactReference} from '@mimir/primitive/artifactReference';
import { isProxyOnlyArtifactReference } from '@mimir/primitive/artifactReference';
import type {
  SyncItemInfo,
  ItemLocalMediaStates,
  LocalMediaState,
  PremiereClipInfo,
  PremiereClipInfoMap,
  PremiereMimirItemInfo,
} from '../fileSync/premiereTypes';
import type { SequenceExportInfo, SequenceInfo } from '@mimir/primitive/premiere/sequenceDetails';
import { syncItemFieldList } from '../fileSync/premiereTypes';
import type { OngoingRestoreStatus } from '../model/ongoingDownload';
import type { Subclip } from '../model/subclip';
import { api_getItems } from '../api/getItemsApi';
import { api_createItem } from '../util/api';
import { constructExportedSequenceFileName } from '../util/fileNames';
import { UploadItem, type UploadContext } from './UploadItem';
import { runTask } from '@mimir/common/async/taskScope';
import type { TransientErrorCookie, TransientErrorHandler } from '@mimir/common/async/taskContext';
import type { DownloadIdentity, DownloadProspect } from '../fileSync/downloadIdentity';
import { planDownload } from '../fileSync/downloadPlanning';
import type { PremiereProjectItemReference, PremiereSequenceReference } from '@mimir/primitive/premiere/premiereReference';
import { proxyArtifactSupported } from '../fileSync/clipInfo';
import { allExceptionDetails, exceptionSummary } from '@mimir/common/util/exception';
import { getArtifactReference, getItemIdFromPremiereClipInfo } from '../fileSync/premiereInterpret';
import { defaultAnalyticConfig, type ArchiveConfig, type UploadConfig } from '@mimir/common/dto/tenantModel';
import { getRouterLink } from '../util/basicRoute';
import { ItemState } from '@mimir/common/dto/itemState';
import { isPremiereMimirItemInfoPlaceholder } from '../util/premiere';
import type { Logger } from '@mimir/common/types/logger';
import { getPathForScriptWith } from './cefEvalEscaping';
import { getBasename, getExtname } from '@mimir/common/platformAbstraction/pathComposition';
import type { ProjectItemUpdate } from '@mimir/primitive/premiere/premiereUpdate';
import cloneDeep from 'lodash/cloneDeep';
import type { AdobePanelContext, ApplicationContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { NotificationOps } from '../fileSync/notificationOps';
import type { BackendClient } from '@mimir/common/util/backendClient';
import type { OngoingDownloadOps } from '../fileSync/ongoingDownloadOps';
import type { ItemWithSourceMedia } from './premiereItemTypes';
import type { LocalStateOps } from '../fileSync/localStateOps';
import type { ExportOps } from '../fileSync/exportOps';
import type { ProjectOps } from '../fileSync/projectOps';
import type { UploadOps } from '../fileSync/uploadOps';
import type { DownloadAndRelinkOps } from '../fileSync/downloadAndRelinkOps';
import { waitForAll } from '@mimir/common/async/async';
import type { ColdStorageRestoreOps } from '../coldStorage/coldStorageRestoreOps';
import { isSegmented } from '@mimir/common/util/path';
import type { LimitedNodeApi } from '@mimir/common/nodejs/limitedNodeApi/nodeApi';

export function getNonMimirClipsToUploadWith(clipInfos: PremiereClipInfoMap, uploadAllPremiereClips: boolean, sequenceExportInfo: SequenceExportInfo | undefined) {
  const nonMimirClipsMap: PremiereClipInfoMap = {};
  if (!sequenceExportInfo || isEmpty(sequenceExportInfo.tracks)) {
    // no tracks, no further checks needed
    return [];
  }

  if (uploadAllPremiereClips) {
    for (const clipInfo of Object.values(clipInfos)) {
      if (clipInfo.artifactRef) {
        // Already a Mimir item - skip
        continue;
      }

      if (clipInfo.projectPath !== sequenceExportInfo.projectPath) {
        // Not a clip in the same project as the sequence - skip
        continue;
      }

      if (!clipInfo.mediaPath) {
        // No physical file to upload - skip
        continue;
      }

      nonMimirClipsMap[clipInfo.mediaPath] = clipInfo;
    }
  } else {
    for (const track of sequenceExportInfo.tracks) {
      if (isEmpty(track.clips)) {
        // no clips in the track
        continue;

      }
      for (const clip of track.clips) {
        const clipInfo = clipInfos[clip.key!];
        if (!clipInfo || clipInfo?.artifactRef || isEmpty(clip.mediaPath) || isEmpty(clip.key)) {
          // already in Mimir, or clip does not have a file to upload. could be something like Bars and Tone,
          // Transparent Video etc. just skip it.
          continue;
        }

        // keyed with mediaPath, so that we eliminate duplicate files to upload
        nonMimirClipsMap[clip.mediaPath!] = clipInfo;
      }
    }
  }

  return Object.values(nonMimirClipsMap);
}

export function getTotalHighresSize(clipInfos: PremiereClipInfo[]) {
  return clipInfos.reduce((total: number, cur: PremiereClipInfo) =>
    total + (cur?.mimirItemLocalMediaStates?.highresState?.fileSize || 0), 0);
}

// Currently this method checks if artifact is not set for a clip that appears segmented from the filename.
export const isClipBroken = (clipInfo: PremiereClipInfo) => (isSegmented(clipInfo.mediaPath) || isSegmented(clipInfo.proxyPath)) && clipInfo.mimirItemId && (!clipInfo.artifactRef || clipInfo.artifactRef.type === 'ImplicitReference');

export function mediaNeedsRelink(clipInfo: PremiereClipInfo, isProxy: boolean) {
  // case for aegraphic files
  if (clipInfo?.preferMediaPathInProject) {
    return false;
  }

  if (!clipInfo?.mimirItemId) {
    return false;
  }

  if (isClipBroken(clipInfo)) {
    return false;
  }

  if (!clipInfo?.mimirItemLocalMediaStates) {
    return false;
  }
  const localMediaState = clipInfo.mimirItemLocalMediaStates[isProxy ? 'proxyState' : 'highresState'];
  if (!localMediaState || !localMediaState.isAvailableLocally) {
    return false;
  }

  // it is available locally
  const pathInProject = clipInfo[isProxy ? 'proxyPath' : 'mediaPath'];

  const pathCanWork = (aLocalPath: string, fileExists: boolean) => !isEmpty(aLocalPath) && aLocalPath === pathInProject && fileExists;

  const { localPath, isAvailableLocally } = localMediaState;
  // Even if path is set correctly, we still need to execute the relinking process if the clip state is offline.
  if (pathCanWork(localPath!, isAvailableLocally) && !clipInfo.isOffline) {
    return false;
  }

  return true;
}

function getPreferredLocalPath(localMediaState?: LocalMediaState) {
  if (localMediaState?.isAvailableLocally) {
    return localMediaState?.localPath;
  }

  return '';
}

interface RelinkParams {
  clipInfo: PremiereClipInfo,
  filePath?: string;
  proxyFilePath?: string;
}

function getRelinkParams(clipInfo: PremiereClipInfo, isProxyEnabled: boolean) {
  if (!clipInfo.mimirItemId) {
    return;
  }

  const relinkParams: RelinkParams = {
    clipInfo,
  };

  relinkParams.filePath = !isProxyEnabled && mediaNeedsRelink(clipInfo, false) ? getPreferredLocalPath(clipInfo.mimirItemLocalMediaStates!.highresState)! : undefined;
  relinkParams.proxyFilePath = isProxyEnabled && mediaNeedsRelink(clipInfo, true) ? getPreferredLocalPath(clipInfo.mimirItemLocalMediaStates!.proxyState)! : undefined;
  if (!relinkParams.filePath && !relinkParams.proxyFilePath) {
    return;
  }

  return relinkParams;
}

async function relinkAllClipsWith(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
  },
  ongoingUploads: UploadItem[],
  clipInfosArray: PremiereClipInfo[],
  isProxyEnabled: boolean,
) {
  const relinkParamsList : RelinkParams[] = [];
  for (const clipInfo of clipInfosArray) {
    const relinkParams = getRelinkParams(clipInfo, isProxyEnabled);
    if (!relinkParams) {
      continue;
    }
    relinkParamsList.push(relinkParams);

  }

  for (const relinkParams of relinkParamsList) {
    services.notificationOps.notifyStartRelinking(relinkParams.clipInfo);
  }

  for (const relinkParams of relinkParamsList) {
    const { filePath, proxyFilePath, clipInfo } = relinkParams;

    services.notificationOps.notifyStartRelinking(clipInfo);

    const newClipInfo = cloneDeep(clipInfo);
    const update : ProjectItemUpdate = { meta: { } };
    // Just having filePath/proxyFilePath set from getRelinkParams is enough to indicate
    // that we need to relink, even if the path is the same as before, because it may be
    // the case that the clip state is offline despite pointing to an available file.
    if (proxyFilePath !== undefined) {
      update.proxyMediaPath = proxyFilePath;
      newClipInfo.proxyPath = proxyFilePath;
    }
    if (filePath !== undefined) {
      update.hiresMediaPath = filePath;
      newClipInfo.mediaPath = filePath;
    }
    if ((update.proxyMediaPath !== undefined) || (update.hiresMediaPath !== undefined)) {
      const cepb = services.applicationContext.cepBridgeClient;
      await cepb.call('updateProjectItem', newClipInfo.itemRef, update);

      // Important to mark it as online here, because this in-memory state is not refreshed
      // from Premiere right away.
      newClipInfo.isOffline = false;
    }
    newClipInfo.isRelinking = false;
    services.notificationOps.notifyPremiereClipInfoUpdated(ongoingUploads, newClipInfo);
  }
}

function relinkClipWith(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
  },
  ongoingUploads: UploadItem[],
  clipInfo: PremiereClipInfo,
  isProxyEnabled: boolean,
) {
  return relinkAllClipsWith(services, ongoingUploads, [clipInfo], isProxyEnabled);
}

function isMimirArtifact(clipInfo: PremiereClipInfo) {
  return clipInfo.artifactRef != null;
}

export const highresNeedsToBeDownloaded = (clipInfo: PremiereClipInfo) => {
  return (clipInfo.artifactRef !== null) &&
    !isProxyOnlyArtifactReference(clipInfo.artifactRef) &&
    !clipInfo.preferMediaPathInProject &&
    !clipInfo.mimirItemLocalMediaStates.highresState!.isAvailableLocally &&
    !clipInfo.mimirItemLocalMediaStates.highresState!.isBeingDownloaded;
};

export const highresDownloadSelectable = (clipInfo: PremiereClipInfo) => {
  return highresNeedsToBeDownloaded(clipInfo) &&
    !clipInfo.mimirItemLocalMediaStates.highresState!.isStateRefreshing;
};

export const highresNeedsToBeRestored = (clipInfo: PremiereClipInfo) => {
  if (clipInfo.mimirItem) {
    return !isMediaOnline(clipInfo.mimirItem);
  }
  return false;
};

export function itemNeedsToBeRestored(clipInfo: PremiereClipInfo) {
  return highresDownloadSelectable(clipInfo) && highresNeedsToBeRestored(clipInfo);
}

export const highresNeedsToBeUploaded = (clipInfo: PremiereClipInfo) => {
  return !isMimirArtifact(clipInfo) &&
    clipInfo.mimirItemLocalMediaStates.highresState!.isAvailableLocally &&
    !clipInfo.mimirItemLocalMediaStates.highresState!.isBeingUploaded;
};

export function getOngoingUploadForClipWith(ongoingUploads: UploadItem[], clipInfo: PremiereClipInfo, includeCompleted: boolean) {
  const localPath = clipInfo?.mimirItemLocalMediaStates?.highresState?.localPath;
  return ongoingUploads.find((ongoingUpload: UploadItem) => {
    const filePath = (ongoingUpload as Partial<PremiereClipUploadItem>).filePath;
    return localPath === filePath && (includeCompleted || !includeCompleted && ongoingUpload.uploadState !== UploadState.COMPLETE);
  });
}

const convertWindowsPathToUnixLike = (filePath: string) => filePath.replace(/\\/g, '/');
export const convertUnixPathToWindowsLike = (filePath: string) => filePath.replace(/\//g, '\\');

export function shouldSkipPathRewriteUnlessNecessaryWith(applicationContext: ApplicationContext, filePath: string) {
  const extensionsToSkipPathRewrite = [
    '.aegraphic'
  ];

  const fileExtension = getExtname(filePath, applicationContext.pathSep);
  return extensionsToSkipPathRewrite.includes(fileExtension.toLowerCase());
}

/**
 * Copy the media to download path immediately after upload completes so that we can save
 * having to redownload it. If that path is read-only, just ignore this silently.
 */
export class PremiereClipUploadItem extends UploadItem {
  constructor(
    private services: {
      applicationContext: AdobePanelContext | ElectronContext,
      notificationOps: NotificationOps,
      logger: Logger,
      uploadContext: UploadContext,
      projectOps: ProjectOps,
    },
    private premiereClipInfos: PremiereClipInfoMap,
    file: FileApi,
    uploadToS3Clone: boolean,
    uploadConfig: Partial<UploadConfig>,
    public clipInfo: PremiereClipInfo,
    private ongoingUploads: UploadItem[],
    private downloadPath: string,
  ) {
    super(services.uploadContext, file, uploadToS3Clone, uploadConfig, clipInfo.mimirItemId);
  }

  get relatedClipMediaPath() {
    return this.clipInfo.mediaPath;
  }

  get filePath() {
    return this.file.path;
  }

  public async onUploadCompleted() {
    await super.onUploadCompleted();
    this.services.logger.info(`Upload Premiere clip completed for item ${this.id}`);

    // set a refreshing indicator since it can take a while for the UI to reflect the subsequent changes in large projects
    const clipInfos: PremiereClipInfo[] = Object.values(this.premiereClipInfos);
    const itemRefsForItemId : PremiereProjectItemReference[] = [];
    for (const clipInfo of clipInfos) {
      if (clipInfo.mimirItemId === this.id) {
        itemRefsForItemId.push(clipInfo.itemRef);
      }
      if (clipInfo.mediaPath === this.filePath) {
        this.services.notificationOps.notifyStartRefreshingMediaState(clipInfo.mimirItemLocalMediaStates.highresState!);
        this.services.notificationOps.notifyPremiereClipInfoUpdated(this.ongoingUploads, {
          ...clipInfo,
          artifactRef: { type: 'FlatFileReference', itemId: this.id },
          mimirItem: uploadItemToPremiereItemInfo(this)
        });
        this.services.notificationOps.notifyStopRefreshingMediaState(clipInfo.mimirItemLocalMediaStates.highresState!);
        itemRefsForItemId.push(clipInfo.itemRef);
      }
    }

    // Q: Why are we using projectOps.setMimirItemIdForMediaPath instead of cepb.call('updateProjectItem') here?
    // A: Because otherwise the needed item ID would not be updated in case of e2e etests with FakeSyncContext.
    const pathForScript = getPathForScriptWith(this.services.applicationContext, this.filePath);
    for (const itemRef of itemRefsForItemId) {
      await this.services.projectOps.setMimirItemIdForMediaPath(pathForScript, this.id, itemRef);
    }

    const downloadPath = this.downloadPath;
    if (!isEmpty(downloadPath) && !shouldSkipPathRewriteUnlessNecessaryWith(this.services.applicationContext, this.filePath!)) {
      try {
        const fileExtension = getExtname(this.filePath!, this.services.applicationContext.pathSep);
        const copyToPath =
          `${downloadPath}${this.services.applicationContext.pathSep}${this.id}${fileExtension}`;

        if (await isFileAvailableLocally(this.services.applicationContext.nodeApi, this.services.logger, copyToPath)) {
          this.services.logger.info(`Skipping copying since already exists: ${copyToPath}`);
        } else {
          this.services.logger.info(`Copying to: ${copyToPath}`);

          // TODO: does not throw error when writing to C:\Program Files\Something, but does not work
          // either, i.e file is not found at destination. same with validateWritability when setting the path. why is that?
          await hardLinkOrCopy(
            this.services.applicationContext.nodeApi,
            this.services.logger,
            this.filePath!,
            copyToPath,
            'overwrite',
            copyFile,
          );
        }

        // Q: Why are we repeating setting the item ID here?
        // A: We just need to update hiresMediaPath here, but updateProjectItem does not work
        // without a meta parameter.
        //
        // Q: Why can't we use setMimirItemIdForMediaPath here?
        // A: Because it does not allow setting hiresMediaPath, which is the one we need to
        // update here.
        //
        // Q: Does that mean this code does not work during e2e tests?
        // A: Yes! The path is not updated in the test project, that that is not a blocker for
        // the test to pass. It is required to set the item ID though, which we already did
        // in the code above.
        //
        // Ideally, we should make these changes:
        // - Allow updateProjectItem to set hiresMediaPath (or other props) without having to set meta.
        // - Extend setMimirItemIdForMediaPath to allow setting hiresMediaPath.
        // - Get rid of passing filePathForScript to setMimirItemIdForMediaPath by making
        //   FakeSyncContext work with itemRef instead.
        // - Or implement ICepBridgeClient to directly manipulate PrprojDocument for e2e tests.
        const cepb = this.services.applicationContext.cepBridgeClient;
        for (const itemRef of itemRefsForItemId) {
          await cepb.call('updateProjectItem', itemRef, {
            meta: {
              'Mimir.Item.Id': this.id,
              'Mimir.Artifact': JSON.stringify({ type: 'ImplicitReference' }),
            },
            hiresMediaPath: copyToPath
          });
        }
      } catch (err) {
        this.services.logger.info(`Ignoring error copying to ${downloadPath}, possibly because of permissions. ${err}`);
      }
    }
  }
}

// the semaphore limit helps prevent stalling the UI when thousands of uploads get queued as a result of clicking "Upload all"
const uploadSemaphore = new PrioritySemaphore(5);

export interface ClipUploadProgressListener {
  onProgress(uploadItem: PremiereClipUploadItem, progress: FileSetProgress): void;
}

async function uploadPremiereFileWith(
  ongoingUploads: UploadItem[],
  premiereClipUploadItem: PremiereClipUploadItem,
  injectIntoSearch: boolean,
  services: {
    uploadManager: BasicUploadManager,
    uploadOps: UploadOps,
    clipUploadProgressListener: ClipUploadProgressListener,
  },
) {
  const { uploadManager, uploadOps } = services;

  const uploadItem = premiereClipUploadItem;
  ongoingUploads.push(uploadItem);
  invariant(uploadItem.file != null, 'File should have been set');

  uploadOps.onPremiereClipUploadStart(uploadItem, injectIntoSearch);
  const priority = makePriority({ urgency: TaskUrgency.LongRunning });

  /*
    Retry forever with exponential backoff, matching web app upload behavior.
  */
  const resilience = new ExponentialBackoff({
    timeLimit: Infinity
  });

  const cts = Axios.CancelToken.source();

  // Track transient errors during retries, matching web app pattern (ItemUpload.vue)
  const transientErrors: string[] = [];

  const listener: FileSetListener = (progress: FileSetProgress) => {
    services.clipUploadProgressListener.onProgress(uploadItem, progress);
    uploadItem.op = { progress, cancel: cts.cancel };

    if (progress.percentage !== undefined) {
      uploadItem.uploadProgress = Math.floor(progress.percentage);
    }
  };

  /*
    TransientErrorHandler to report transient errors during retries.
  */
  const transientErrorHandler: TransientErrorHandler = {
    setTransientError: (error: any): TransientErrorCookie => {
      const msg = exceptionEssence(error);
      transientErrors.push(msg);
      return msg;
    },
    clearTransientError: (cookie: TransientErrorCookie) => {
      const msg = cookie as string;
      transientErrors.splice(transientErrors.indexOf(msg), 1);
    }
  };

  await runTask({ priority, resilience, cancelToken: cts.token, transientErrorHandler }, (tc) =>
    uploadSemaphore.limit(
      () =>
        uploadManager.uploadFileBatchForItem(
          tc,
          () => uploadItem.permanentId,
          [uploadItem],
          listener
        ),
      tc.priority,
      tc.cancelToken
    ));
}


/**
 * Tries to construct and return a download identity. Returns null if
 * constructing a download identity requires a Mimir item that is missing
 * from the given clip info. This can happen in case the user opens an old project
 * containing an implicit reference to some segmented media and we try to construct
 * an identity before the associated Mimir item has been fetched.
 */
export function tryMakeDownloadIdentityForClipInfo(artefact: ItemArtefact, clipInfo: PremiereClipInfo): DownloadIdentity | null {
  const artifactReference = getArtifactReference(clipInfo);
  const itemId = getItemIdFromPremiereClipInfo(clipInfo);
  if (itemId === null) {
    return null;
  }

  const dlId: DownloadIdentity = {
    itemId,
    artefact,
    artifactReference
  };

  if (!tryNormalizeDownloadIdentity(dlId, clipInfo.mimirItem)) {
    return null;
  }
  return dlId;
}


export function makeDownloadIdentityForItem(
  artefact: ItemArtefact,
  item: Pick<ItemWithSourceMedia, 'id' | 'media'>,
  artifactReference: ArtifactReference
): DownloadIdentity {
  const dlId: DownloadIdentity = {
    itemId: item.id,
    artefact,
    artifactReference,
  };

  tryNormalizeDownloadIdentity(dlId, item);
  return dlId;
}


/**
 * Adjusts the given download identity to:
 * 1) avoid using a segment suffix when looking up for an aaf file
 * 2) convert the ImplicitReference to a TsSegmentRange or AudioTsSegmentRange
 *    when the item's artifact is actually segmented
 *
 * Returns true if the normalization was either unnecessary or successful; returns false
 * if the normalization was not feasible for lack of a Mimir item to get additional information from.
 */
function tryNormalizeDownloadIdentity(dlId: DownloadIdentity, item: Pick<ItemWithSourceMedia, 'id' | 'media' | 'originalFileName' | 'ingestSourceFullPath'> | undefined): boolean {
  if (dlId.artefact === 'aaf') {
    dlId.artifactReference = { type: 'ImplicitReference' };
    return true;
  }

  if (dlId.artifactReference.type !== 'ImplicitReference') {
    // We already have a good artifact reference
    return true;
  }

  if (item === undefined) {
    // We cannot normalize an implicit reference without an item
    return false;
  }


  if (item.media?.codecsByStreamId[StreamRoleId.LiveRecordAudioOnlyHighresTs] !== undefined) {
    dlId.artifactReference = {
      type: 'AudioTsSegmentRange',
      itemId: item.id,
      segmentTimes: {
        first: 0,
        last: item.media.segmentCount - 1,
      },
      remuxTo: undefined,
    };
    return true;
  }

  if ((dlId.artefact === 'highres' && item.media?.codecsByStreamId[StreamRoleId.LiveRecordHighresTs])
       || (dlId.artefact === 'proxy' && item.media?.codecsByStreamId[StreamRoleId.LiveRecordWebproxyTs])) {
    dlId.artifactReference = {
      type: 'TsSegmentRange',
      itemId: item.id,
      segmentTimes: {
        first: 0,
        last: item.media.segmentCount - 1,
      },
      highResExt: undefined,
    };
  }
  return true;
}


export async function getLocalMediaStatesForClip(
  localStateOps: LocalStateOps,
  clipInfo: PremiereClipInfo,
  forceRefresh: boolean,
): Promise<ItemLocalMediaStates | null> {
  const dlId = tryMakeDownloadIdentityForClipInfo('highres', clipInfo);
  if (dlId === null) {
    return null;
  }
  const item = clipInfo.mimirItem ?? null;
  if (isPremiereMimirItemInfoPlaceholder(item) || (!clipInfo.needsRefreshingLocalMediaStates && !forceRefresh)) {
    return null;
  }

  return getItemLocalMediaStatesWith(localStateOps, item, null, dlId);
}

export async function getItemLocalMediaStatesWith(
  localStateOps: LocalStateOps,
  item: ItemWithSourceMedia | null,
  subclip: Subclip | null,
  dlId: DownloadIdentity
): Promise<ItemLocalMediaStates> {
  const [highresState, proxyState] = await Promise.all([
    localStateOps.getItemArtefactLocalMediaState({ ...dlId, artefact: 'highres' }, item, subclip),
    localStateOps.getItemArtefactLocalMediaState({ ...dlId, artefact: 'proxy' }, item, subclip)]);

  const result: ItemLocalMediaStates = {
    highresState,
    proxyState,
  };

  return result;
}

export const proxyNeedsToBeDownloaded = (clipInfo: PremiereClipInfo) => {
  return isMimirArtifact(clipInfo) &&
  proxyArtifactSupported(clipInfo) &&
    !clipInfo.mimirItemLocalMediaStates.proxyState!.isAvailableLocally &&
    !clipInfo.mimirItemLocalMediaStates.proxyState!.isBeingDownloaded;
};

export interface RelinkOrUploadSyncOp {
  type: 'relink' | 'upload';
}

export interface DownloadSyncOp {
  type: 'highres' | 'proxy';
  alsoDownloadHighres: boolean;
  isPremiereStandaloneMode: boolean;
  isProxyWorkflowEnabled: boolean;
}

export type SyncOperation = RelinkOrUploadSyncOp | DownloadSyncOp;

function isDownloadSyncOp(syncOp: SyncOperation): syncOp is DownloadSyncOp {
  return syncOp.type == 'highres' || syncOp.type == 'proxy';
}

/**
 * Even through the Mimir user interface may offer the user the ability to
 * choose to download a proxy artefact, such a choice is not always appropriate.
 *
 * A request for downloading proxy may also be augmented to request that the
 * highres should also be downloaded at the same time.
 *
 * Therefore we distinguish between a *requested* artefact in a DownloadSyncOp,
 * and a *resolved* list of item artefacts that we will actually attempt to
 * download.
 */
export function resolveArtefactTypes(syncOp: SyncOperation, clipInfo: PremiereClipInfo): ItemArtefact[] {
  // Only download operations can request proxy.
  if (!isDownloadSyncOp(syncOp)) {
    return ['highres'];
  }

  /* Only video items support proxy artefacts. */
  if (clipInfo.mimirItem?.itemType != ItemType.VIDEO) {
    return ['highres'];
  }

  if (syncOp.type == 'proxy') {
    if (syncOp.alsoDownloadHighres) {
      return ['proxy', 'highres'];
    }

    return ['proxy'];
  }

  return ['highres'];
}

/*
  TODO: It does not make sense for this function to resolve a sync operation
  request to a concrete artefact to sync, since in the case where we resolve to
  a highres artefact, the function has a precondition that the item must have
  been restored already.

  For a caller to make sure that this precondition is met, it must already have
  done the sync operation request to concrete artefact resolution itself. And if
  that is the case, it doesn't make sense for syncClipWith() to reimplement this
  functionalty.
 */
export async function syncClipWith(
  services: {
    notificationOps: NotificationOps,
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    userDataSystemPath: string,
  },
  injectIfUpload: boolean,
  premiereClipInfos: PremiereClipInfoMap,
  ongoingUploads: UploadItem[],
  clipInfo: PremiereClipInfo,
  operation: SyncOperation,
  prefetched: Record<string, SyncItemInfo> | null,
  itemToUpload: PremiereClipUploadItem | undefined,
  toFolderId?: string,
  ongoingRestoreStatus?: OngoingRestoreStatus,
) {
  if (!clipInfo.mimirItemLocalMediaStates || isClipBroken(clipInfo)) {
    return;
  }

  if (operation.type == 'relink') {
    await relinkClipWith(services, ongoingUploads, clipInfo, config.isProxyMode);
    return;
  }

  // Can't sync (download) if item not visible to user
  if (clipInfo.isNotVisibleToUser || clipInfo.isInRecycleBin) return;

  if (operation.type == 'upload') {
    if (!highresNeedsToBeUploaded(clipInfo)) {
      return;
    }
    if (getOngoingUploadForClipWith(ongoingUploads, clipInfo, false)) {
      // skip upload if the same file is being uploaded already
      return;
    }

    const premiereClipUploadItem = itemToUpload ?? createUploadItemForClip(clipInfo, premiereClipInfos, toFolderId, ongoingUploads, config, services);

    // Set the mogrtParentItemId on the uploadItem if available. This is used
    // later from beforeUpload in order to create a relation with this child
    // item and the mogrt parent item.
    if (clipInfo.mimirMogrtParentItemId) {
      premiereClipUploadItem.mogrtParentItemId = clipInfo.mimirMogrtParentItemId;
    }

    const noopProgressListener: ClipUploadProgressListener = {
      onProgress: () => {}
    };
    const uploadServices = {
      clipUploadProgressListener: noopProgressListener,
      ...services,
    };
    return await doUploadSyncClipWith(
      premiereClipUploadItem,
      ongoingUploads,
      injectIfUpload,
      uploadServices,
    );
  }

  invariant(isDownloadSyncOp(operation), 'Expected a download operation');
  if (!isMimirArtifact(clipInfo)) {
    return;
  }

  /*
    It is important only to start refreshing the media state after we've
    determined that there is an operation we need to do. Since otherwise the
    the media cell will show an "access_time" icon.
  */
  const mediaState = mediaStateForSyncOp(operation, clipInfo);
  services.notificationOps.notifyStartRefreshingMediaState(mediaState!);
  const resolvedArtefacts = resolveArtefactTypes(operation, clipInfo);

  const dlOp = operation;
  const itemId = getItemIdFromPremiereClipInfo(clipInfo);
  invariant(itemId, 'Expected clipInfo to resolve to itemId');
  const itemRef = clipInfo.itemRef;
  for (const artefact of resolvedArtefacts) {
    const needsToBeDownloaded = artefact == 'highres' ? highresNeedsToBeDownloaded(clipInfo) : proxyNeedsToBeDownloaded(clipInfo);
    if (!needsToBeDownloaded) {
      // No need to download but relink anyway to update media path.
      await relinkClipWith(services, ongoingUploads, clipInfo, artefact == 'proxy');
      continue;
    }

    if (artefact === 'highres' && clipInfo.mimirItem?.highresDeletion && wasHighresDeleted(clipInfo.mimirItem)) {
      // Skip highres download if deleted
      continue;
    }

    const downloadIdentity = tryMakeDownloadIdentityForClipInfo(artefact, clipInfo);
    if (downloadIdentity === null) {
      continue;
    }
    await doDownloadAndRelink(services, dlOp, downloadIdentity, artefact, prefetched, itemRef, ongoingRestoreStatus);
  }

  if (clipInfo.mimirItem !== undefined) {
    // refresh states to reflect in UI
    const dlId = tryMakeDownloadIdentityForClipInfo('highres', clipInfo);
    invariant(dlId !== null, 'Unexpected null dlId value since clipInfo.mimirItem is defined');
    clipInfo.mimirItemLocalMediaStates = await getItemLocalMediaStatesWith(services.localStateOps, clipInfo.mimirItem, null, dlId);
  }
  services.notificationOps.notifyStopRefreshingMediaState(mediaState!);
  services.notificationOps.notifyPremiereClipInfoUpdated(ongoingUploads, clipInfo);
}


export async function doUploadSyncClipWith(
  uploadItem: PremiereClipUploadItem,
  ongoingUploads: UploadItem[],
  injectIntoSearch: boolean,
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    clipUploadProgressListener: ClipUploadProgressListener,
  },
) {
  const clipInfo = uploadItem.clipInfo;
  const highresState = clipInfo.mimirItemLocalMediaStates.highresState;
  invariant(highresState, 'Expected highres state for upload');
  try {
    highresState.isBeingUploaded = true;
    await uploadPremiereFileWith(
      ongoingUploads,
      uploadItem,
      injectIntoSearch,
      services,
    );
  } catch (error) {
    services.logger.info(`Error while uploading clips: ${error}`);
    if (!Axios.isCancel(error)) {
      services.notificationOps.displayBriefError(`Error while uploading clip: ${exceptionEssence(error)}`);
    }
    if (clipInfo.mimirItemLocalMediaStates.highresState?.isBeingUploaded) {
      clipInfo.mimirItemLocalMediaStates.highresState.isBeingUploaded = false;
    }
    throw error;
  } finally {
    highresState.isBeingUploaded = false;
    services.notificationOps.notifyPremiereClipInfoUpdated(ongoingUploads, clipInfo);
  }
}

export function createUploadItemForClip(
  clipInfo: PremiereClipInfo,
  premiereClipInfos: PremiereClipInfoMap,
  toFolderId: string | undefined,
  ongoingUploads: UploadItem[],
  config: {
    downloadPath: string,
  },
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    uploadContext: UploadContext,
    projectOps: ProjectOps,
  }
): PremiereClipUploadItem {
  const mediaState = mediaStateForSyncOp({ type: 'upload' }, clipInfo);
  invariant(mediaState, 'Expected at least one resolved artefact');
  const filePath = mediaState.localPath;
  invariant(filePath != null, 'Media state must have localPath to attempt upload');

  const fileName = getBasename(filePath, services.applicationContext.pathSep);
  const file: FilePath = {
    name: fileName,
    path: filePath,
    size: getLocalFileSize(services.applicationContext.nodeApi, filePath)
  };
  const uploadConfig: Partial<UploadConfig> = {
    ...clipInfo.analyticConfig,
    folderId: toFolderId
  };
  return new PremiereClipUploadItem(services, premiereClipInfos, file, false, uploadConfig, clipInfo, ongoingUploads, config.downloadPath);
}


function mediaStateForSyncOp(
  op: SyncOperation,
  clipInfo: PremiereClipInfo,
) {
  const resolvedArtefacts = resolveArtefactTypes(op, clipInfo);
  const { proxyState, highresState } = clipInfo.mimirItemLocalMediaStates;
  const mediaState = resolvedArtefacts[0] == 'proxy' ? proxyState : highresState;
  invariant(mediaState, 'Expected at least one resolved artefact');
  return mediaState;
}

export function planSyncDownload(
  applicationContext: ApplicationContext,
  dlOp: DownloadSyncOp,
  downloadIdentity: DownloadIdentity,
): DownloadProspect {
  return planDownload(applicationContext, {
    isProxyWorkflowEnabled: dlOp.isProxyWorkflowEnabled,
    isPremiereStandaloneMode: dlOp.isPremiereStandaloneMode,
    downloadIdentity,
  });
}


async function doDownloadAndRelink(
  services: {
    applicationContext: AdobePanelContext | ElectronContext;
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  dlOp: DownloadSyncOp,
  downloadIdentity: DownloadIdentity,
  resolvedArtefact: ItemArtefact,
  prefetched: Record<string, SyncItemInfo> | null,
  itemRef: PremiereProjectItemReference | null,
  ongoingRestoreStatus?: OngoingRestoreStatus,
) {
  const plan = planSyncDownload(services.applicationContext, dlOp, downloadIdentity);
  if (plan.blocker != null) {
    throw makeError(plan.blocker, 'DownloadBlocked');
  }

  if (itemRef) {
    plan.deliver = {
      type: 'DeliverPremiereItemUpdate',
      itemRef
    };
  }
  plan.dlId.artefact = resolvedArtefact;

  await services.downloadAndRelinkOps.downloadAndRelink(dlOp.isProxyWorkflowEnabled, dlOp.isPremiereStandaloneMode, plan, prefetched, ongoingRestoreStatus);
}

export async function restoreAndSyncClip(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    coldStorageRestoreOps: ColdStorageRestoreOps,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  premiereClipInfos: PremiereClipInfoMap,
  ongoingUploads: UploadItem[],
  clipInfo: PremiereClipInfo,
  operation: DownloadSyncOp & { type: 'highres' },
  prefetched: Record<string, SyncItemInfo> | null,
  toFolderId?: string,
) {
  if (!clipInfo.mimirItemId) {
    services.logger.info('Missing mimirItemId');
    return;
  }
  if (!config.archiveConfig) {
    services.logger.info('Missing archive config, cannot proceed restore before downloading');
    return;
  }
  const restoreMediaModel: RestoreMediaModel = {
    itemId: clipInfo.mimirItemId,
    tier: config.archiveConfig.defaultRestoreTier,
    retentionDays: config.archiveConfig.defaultRetentionDays,
  };

  let restoreMediaResult: ItemRestoring | null;
  const coldStorageRestoreResponse = await services.coldStorageRestoreOps.requestRestore(restoreMediaModel.itemId, restoreMediaModel.tier, restoreMediaModel.retentionDays);
  if (coldStorageRestoreResponse.objectOnline) {
    restoreMediaResult = {
      itemId: restoreMediaModel.itemId,
      // If the object is actually online, the restore tier does not matter so let's just
      // reuse the requested value
      tier: restoreMediaModel.tier,
    };
  } else if (coldStorageRestoreResponse.restoreStatus.restoreWasAlreadyInProgress) {
    // If the object was already being restored, the legacy code returns a null result,
    // so let's do the same
    restoreMediaResult = null;
  } else {
    restoreMediaResult = {
      itemId: restoreMediaModel.itemId,
      tier: coldStorageRestoreResponse.restoreStatus.effectiveRestoreTier,
    };
  }

  if (restoreMediaResult) {
    const ongoingRestoreStatus: OngoingRestoreStatus = {
      tier: restoreMediaResult.tier,
      retentionDays: restoreMediaModel.retentionDays,
      showUpgradeOption: restoreMediaModel.tier === RestoreTier.Expedited && restoreMediaResult.tier !== RestoreTier.Expedited,
      isUpgrading: false,
    };
    await syncClipWith(services, config, false, premiereClipInfos, ongoingUploads, clipInfo, operation, prefetched, undefined, toFolderId, ongoingRestoreStatus);
  }
}


export async function syncClipsWith(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    backendClient: BackendClient,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    coldStorageRestoreOps: ColdStorageRestoreOps,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  injectIfUpload: boolean,
  // All the clips
  premiereClipInfos: PremiereClipInfoMap,
  ongoingUploads: UploadItem[],
  // The clips to process
  clipInfosArray: PremiereClipInfo[],
  operation: SyncOperation,
  itemsToUpload: PremiereClipUploadItem[] | undefined,
  toFolderId?: string,
) {
  // it is better to do it as a bulk call, instead of parallel promises,
  // in order to reduce the message queue built up from project changed events being fired for each relink.
  if (operation.type == 'relink') {
    return relinkAllClipsWith(services, ongoingUploads, clipInfosArray, config.isProxyMode);
  }

  let prefetched: Record<string, SyncItemInfo> = {};
  let requestedProxy = false;
  if (isDownloadSyncOp(operation)) {
    requestedProxy = operation.type == 'proxy';
    const itemIds = compact(clipInfosArray.map(clip => clip.mimirItemId));
    if (itemIds.length) {
      const items = await api_getItems(services.backendClient, itemIds, syncItemFieldList) as SyncItemInfo[];
      prefetched = keyBy(items, i => i.id);
    }
  }

  const promises: Promise<unknown>[] = [];
  // Only create download notification, if the operation is a download/sync op
  const notifier = isDownloadSyncOp(operation) ? services.notificationOps.createNotifier('Downloading items', '', undefined) : undefined;

  for (const clipInfo of clipInfosArray) {
    const needsToBeRestored = itemNeedsToBeRestored(clipInfo);

    if (needsToBeRestored && isDownloadSyncOp(operation)) {
      const resolvedArtefacts = resolveArtefactTypes(operation, clipInfo);
      if (resolvedArtefacts.includes('highres')) {
        /*
          TODO: Pass in already resolved artefact instead of constructing dummy
          operation here.
        */
        const requestedHighresOp: DownloadSyncOp & { type: 'highres' } = { ...operation, type: 'highres' };
        promises.push(restoreAndSyncClip(services, config, premiereClipInfos, ongoingUploads, clipInfo, requestedHighresOp, prefetched, toFolderId));
      }
    }

    if (!needsToBeRestored || requestedProxy) {
      const itemToUpload = itemsToUpload && itemsToUpload.find(item => item.relatedClipMediaPath === clipInfo.mediaPath);
      promises.push(syncClipWith(services, config, injectIfUpload, premiereClipInfos, ongoingUploads, clipInfo, operation, prefetched, itemToUpload, toFolderId, undefined));
    }
  }

  // Wait for all promises to be either rejected or resolved. Do not stop at the first rejection, because we don't want
  // one bad clip to stop the syncing of other possibly good clips.
  await waitForAll(promises);

  if (notifier) {
    notifier.setCalculating(false);
  }
}


export function uploadDependenciesWith(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    backendClient: BackendClient,
    uploadContext: UploadContext,
    uploadManager: BasicUploadManager,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    coldStorageRestoreOps: ColdStorageRestoreOps,
    projectOps: ProjectOps,
    uploadOps: UploadOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    isProxyMode: boolean,
    downloadPath: string,
    archiveConfig: ArchiveConfig | undefined,
    userDataSystemPath: string,
  },
  premiereClipInfos: PremiereClipInfoMap,
  ongoingUploads: UploadItem[],
  uploadAllPremiereClips: boolean,
  sequenceExportInfo: SequenceExportInfo | undefined,
  folderId: string | undefined,
  itemsToUpload: PremiereClipUploadItem[] | undefined,
): {
  clipsToUpload: PremiereClipInfo[];
  uploadPromise: Promise<void> | undefined;
  operationConclusionToken: CancelToken | undefined;
} {
  const allNonMimirClips = getNonMimirClipsToUploadWith(premiereClipInfos, uploadAllPremiereClips, sequenceExportInfo);
  if (allNonMimirClips.length === 0) {
    return { clipsToUpload: [], uploadPromise: undefined, operationConclusionToken: undefined };
  }

  const operationConclusion = Axios.CancelToken.source();
  const uploadPromise = syncClipsWith(services, config, false, premiereClipInfos, ongoingUploads, allNonMimirClips, { type: 'upload' }, itemsToUpload, folderId).then(() => operationConclusion.cancel('concluded'));
  return { uploadPromise, clipsToUpload: allNonMimirClips, operationConclusionToken: operationConclusion.token };
}

export async function getSequenceExportInfoWith(
  services: {
    notificationOps: NotificationOps,
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
    exportOps: ExportOps,
  },
  premiereClipInfos: PremiereClipInfoMap,
  sequenceRef: PremiereSequenceReference,
  presetPath: string | null,
  suppressErrorToast: boolean = false,
): Promise<SequenceInfo> {
  let seqInfo: SequenceInfo;
  try {
    seqInfo = await services.exportOps.getSequenceExportInfo(sequenceRef, presetPath ?? '');
  } catch (err) {
    services.logger.error(`getSequenceExportInfo: Error in retrieving sequence info: ${allExceptionDetails(err)}`);
    services.notificationOps.displayBriefError('Error in retrieving sequence: ' + exceptionSummary(err));
    throw err;
  }

  const exportResult = seqInfo.exportInfo;

  if (exportResult && exportResult.tracks && exportResult.tracks.length) {
    correctInPointOfImageClips(exportResult, premiereClipInfos);
  }

  if (exportResult && exportResult.errorMessage && !suppressErrorToast) {
    services.notificationOps.displayBriefError(exportResult.errorMessage);
    throw new Error(exportResult.errorMessage);
  }

  return seqInfo;
}

/**
 * We have observed that in the clip info we get from Premiere the
 * "inPoint" and "outPoint" of image clips are set to large nonsensical
 * values, so we manually overwrite these.
 */
function correctInPointOfImageClips(sequenceInfo: SequenceExportInfo, clipInfos: PremiereClipInfoMap) {
  const clipItemType = (clipKey: string | undefined): DynamoItemType | null => {
    if (!clipKey) {
      return null;
    }
    const clipInfo = clipInfos[clipKey];
    const itemType = clipInfo?.mimirItem?.itemType;
    if (itemType && itemType !== 'folder') {
      /*
        TODO: We actually never expect itemType folder here. The need for this
        check is a quirk of the mimirItem type definition. Once we fix this type
        issue, we can remove the (itemType !== 'folder') check.
      */
      return itemType;
    } else if (clipInfo?.mimirItemId && clipInfo.mediaPath) {
      // If this is a mimir item we should normally get into the
      // above if case where we have looked up the item type already.
      // But if not (for example if the getItem call has failed)
      // we fall back to looking at the file extension.
      return itemTypeFromFilename(clipInfo.mediaPath);
    }
    return null;
  };

  for (const track of sequenceInfo.tracks) {
    for (const clip of track.clips) {
      if (clipItemType(clip.key) === ItemType.IMAGE) {
        clip.inPoint = 0;
        clip.outPoint = clip.duration ?? 0;
      }
    }
  }
}

/**
 * Updates item's existing sequence information with given info.
 * Called each time export preset is changed.
 * @param uploadItem Item object to be uploaded
 * @param sequenceInfo Export sequence information retrieved from premiere panel code
 */
export const assignSequenceParameters = (uploadItem: UploadItem, sequenceInfo: SequenceExportInfo) => {
  uploadItem.parentItemIds = sequenceInfo.parentItemIds;
  if (sequenceInfo.exportedFileExtension?.length > 0) {
    const exportedFileMediaType = mediaTypeFromFilename(`.${sequenceInfo.exportedFileExtension}`);
    uploadItem.mediaType = exportedFileMediaType;
    uploadItem.derivedItemType = itemTypeFromFilename(exportedFileMediaType);

    /*
      TODO: The property this.file.name is only the basename of the file, which
      means that any "/" or "\\" inside of this string is intended to be part of
      the basename, and not be a path separator.

      It is therefore technically incorrect to call
      constructExportedSequenceFileName() here which transitively performs
      another getBasename() call with a given path separator, which may differ
      from the original context where the basename was captured.

      We should instead call a variant of constructExportedSequenceFileName()
      that does not do any additional interpretation of path separators.
    */
    const pathSep = '/';
    uploadItem.file = {
      ...uploadItem.file,
      name: constructExportedSequenceFileName(uploadItem.file.name, pathSep, uploadItem.metadata, sequenceInfo.exportedFileExtension),
    };
  }

  if (!isEmpty(sequenceInfo.tracks)) {
    uploadItem.sequenceDetails = {
      projectType: 'prproj',
      tracks: sequenceInfo.tracks,
      sequenceInPointInMs: sequenceInfo.sequenceInPointInMs,
      sequenceOutPointInMs: sequenceInfo.sequenceOutPointInMs,
    };
  }
};

export async function makeDirWith(
  services: {
    logger: Logger,
    applicationContext: AdobePanelContext | ElectronContext,
  },
  dirPath: string,
) {
  if (await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, dirPath)) {
    return;
  }

  // On windows, makedir does not work for multi level dirs without having backslashes.
  if (services.applicationContext.isWindows) {
    dirPath = convertUnixPathToWindowsLike(dirPath);
  }

  services.logger.info(`recursiveMkdirSync: ${dirPath}`);
  recursiveMkdirSync(services.applicationContext.nodeApi, dirPath);
}

function getMimirDataPath(applicationContext: ApplicationContext, userDataSystemPath: string): string {
  return `${userDataSystemPath}${applicationContext.pathSep}mimir-premiere-panel`;
}

export function getDirPathInMimirDataPath(applicationContext: ApplicationContext, userDataSystemPath: string, dirName: string) {
  const mimirDataPath = getMimirDataPath(applicationContext, userDataSystemPath);
  return `${mimirDataPath}${applicationContext.pathSep}${dirName}`;
}

async function getDirInUserData(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  userDataSystemPath: string,
  dirName: string,
): Promise<string> {
  const mimirDataPath = getMimirDataPath(services.applicationContext, userDataSystemPath);
  const dirPath = getDirPathInMimirDataPath(services.applicationContext, userDataSystemPath, dirName);

  if (!await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, dirPath)) {
    services.logger.info(`Dir not found at ${dirPath}. Creating...`);
    await makeDirWith(services, mimirDataPath);
    await makeDirWith(services, dirPath);

    if (!await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, dirPath)) {
      const msg = `Dir could not be created at ${dirPath}`;
      services.logger.error(msg);
      throw new Error(msg);
    }
  }

  return dirPath;
}

export const TEMP_FOLDER_NAME = 'temp';

/**
 * We ask Premiere to get user data dir which we can use as scratch/temp space.
 * We don't use os.tmpdir() since that is seen to return '\tmp' on windows which cannot be given
 * to AME. Also, by using our own temp dir, we can control when to purge it.
 */
export function getTempDirInUserDataWith(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    logger: Logger,
  },
  userDataSystemPath: string,
): Promise<string> {
  return getDirInUserData(services, userDataSystemPath, TEMP_FOLDER_NAME);
}

export function readAsBrowserFile(adobeContext: AdobePanelContext | ElectronContext, filePath?: string): FilePath {
  if (!filePath) {
    throw new Error('no filePath');
  }

  const filename = getBasename(filePath, adobeContext.pathSep);

  if (adobeContext.isWindows) {
    filePath = convertWindowsPathToUnixLike(filePath);
  }

  return {
    name: filename,
    path: filePath,
    size: getLocalFileSize(adobeContext.nodeApi, filePath),
  };
}


/**
 * When deleteAfterwards is true (local render to temp dir) then we need to clean up on completion or error
 */
export class PremiereSequenceUploadItem extends UploadItem {
  public deleteAfterwards = false;

  constructor(private nodeApi: LimitedNodeApi, context: UploadContext, file: FileApi, uploadToS3Clone: boolean, config: Partial<UploadConfig> = {}, itemId?: string) {
    super(context, file, uploadToS3Clone, config, itemId);
  }

  public async onUploadCompleted() {
    await super.onUploadCompleted();
    this.cleanUp();
  }

  public async onUploadError() {
    await super.onUploadError();
    this.cleanUp();
  }

  private cleanUp() {
    if (this.deleteAfterwards) {
      deleteFile(this.nodeApi, this.file.path!);
    }
  }
}

export const createPlaceholderForClip = async (
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    backendClient: BackendClient,
    projectOps: ProjectOps,
  },
  ongoingUploads: UploadItem[],
  clip: PremiereClipInfo,
  targetFolderId: string | undefined,
  taskUrgency: TaskUrgency,
  itemToUpload: PremiereClipUploadItem | undefined,
) => {
  const clipFilePath = clip.mimirItemLocalMediaStates.highresState?.localPath ?? clip.mediaPath;
  const clipFileName = getBasename(clipFilePath, services.applicationContext.pathSep);
  const priority = makePriority({ urgency: taskUrgency });
  const itemCreationResponse = itemToUpload ? await itemToUpload.beforeUpload(priority) : undefined;
  const placeholderResponse = itemCreationResponse || await api_createItem(
    services.backendClient,
    clipFileName,
    clipFileName,
    mediaTypeFromFilename(clipFileName),
    defaultAnalyticConfig(),
    targetFolderId ? [targetFolderId] : null,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    {
      priority: priority
    }
  );
  const pathForScript = getPathForScriptWith(services.applicationContext, clipFilePath);
  await services.projectOps.setMimirItemIdForMediaPath(pathForScript, placeholderResponse.id, clip.itemRef);
  services.notificationOps.notifyPremiereClipInfoUpdated(ongoingUploads, {
    ...clip,
    mimirItemId: placeholderResponse.id,
    mimirItem: {
      ...placeholderResponse,
      itemState: ItemState.NEW
    }
  });
};

/**
 * Create updated Premiere item object from an 'UploadItem' that has been completed
 */
const uploadItemToPremiereItemInfo = (uploadItem: UploadItem): PremiereMimirItemInfo => {
  const itemType = itemTypeFromMediaType(uploadItem.mediaType);
  return {
    id: uploadItem.id,
    originalFileName: uploadItem.file.name,
    itemType,
    link: getRouterLink(itemType, { id: uploadItem.id }, { returnTo: 'premiere' }),
    createdBy: uploadItem.createdBy,
    itemState: ItemState.HIGHRES_AVAILABLE
  };
};
